'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@ahafood/ui/components/carousel';
import { Image } from '@ahafood/ui/components/image';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import Autoplay from 'embla-carousel-autoplay';
import React, { useState, useCallback, useEffect } from 'react';

import { trackEvent } from '@/lib/event-tracking';
import { useAIPlannerStore } from '@/store/ai-planner';

const INTRO_BENEFITS = [
  {
    icon: `${assetPrefix}/static/icons/food-icon.svg`,
    title: 'Chọn món theo sở thích và đặt mỗi ngày',
    iconBg: 'bg-green-500/20',
    iconColor: 'text-green-400',
    description: 'AI tìm món và đặt bữa trưa đúng lịch đã thiết lập.',
  },
  {
    icon: `${assetPrefix}/static/icons/fast-time.svg`,
    title: 'Giao đúng giờ, không lo phí ship',
    description:
      'Ưu tiên tìm xế, trợ lý AI sẽ thông báo khi gần đến giờ ăn và khi bữa trưa sắp đến.',
    iconBg: 'bg-orange-500/20',
    iconColor: 'text-orange-400',
  },
  {
    icon: `${assetPrefix}/static/icons/lock.svg`,
    title: 'Bạn luôn toàn quyền kiểm soát',
    description: 'Có thể chỉnh hoặc tạm dừng bất cứ lúc nào.',
    iconBg: 'bg-blue-500/20',
    iconColor: 'text-blue-400',
  },
];

const INTRO_STEPS = [
  {
    number: 1,
    title: 'Xem trước kế hoạch',
    description: 'Xem lịch quán ăn, khẩu phần ăn & chi phí dự kiến',
  },
  {
    number: 2,
    title: 'Khởi động kế hoạch theo ngày',
    description:
      'Nhập thông tin giao hàng, ngân sách. AI tự động tìm món và đặt món theo kế hoạch',
  },
];

function IntroSlide1() {
  return (
    <div className="flex flex-col size-full min-h-[250px] px-4">
      {/* Title */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-[#ff8200] text-left">
          Trợ lý bữa trưa tự động
        </h3>
        <Image
          src={`${assetPrefix}/static/images/intro-food-icon.webp`}
          alt="AI Planner Intro Icon"
          width={87}
          height={41}
          color="white"
        />
      </div>

      {/* Benefits */}
      <div className="space-y-4 flex-1">
        {INTRO_BENEFITS.map((benefit, index) => (
          <div key={index} className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-white font-medium text-sm flex items-center gap-2">
                <Image
                  src={benefit.icon}
                  alt={benefit.title}
                  width={20}
                  height={20}
                  color="white"
                />{' '}
                {benefit.title}
              </p>
              {benefit.description && (
                <p className="text-white/60 text-xs mt-1">
                  {benefit.description}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function IntroSlide2() {
  return (
    <div className="flex flex-col size-full min-h-[250px] px-4">
      {/* Title */}
      <h3 className="text-xl font-bold text-[#ff8200] text-center mb-6">
        Chỉ với 2 bước
      </h3>

      {/* Steps */}
      <div className="space-y-4 flex-1">
        {INTRO_STEPS.map((step) => (
          <div
            key={step.number}
            className="bg-white/5 rounded-xl p-4 border border-white/10"
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 size-8 rounded-full bg-[#ff8200] flex items-center justify-center">
                <span className="text-white font-bold text-sm">
                  {step.number}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold text-sm">{step.title}</p>
                <p className="text-white/60 text-xs mt-1">{step.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function IntroScreen() {
  const [api, setApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);
  const nextStep = useAIPlannerStore.use.nextStep();

  // Track current slide
  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrentSlide(api.selectedScrollSnap());
    };

    api.on('select', onSelect);
    onSelect();

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  const scrollTo = useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api]
  );

  const handleStart = useCallback(() => {
    trackEvent('meal_plan', {
      event_name: 'click_button',
      button_name: 'meal_plan_start',
    });
    nextStep();
  }, [nextStep]);

  return (
    <div className="h-full flex flex-col items-center -mx-4">
      {/* Carousel - takes maximum available space */}
      {/* Top Section - grows to fill space */}
      <ScrollArea className="flex-1 w-full max-h-[calc(100dvh-123px-86px)] overflow-auto">
        <div className="relative w-full h-[260px] flex items-center justify-center py-4">
          <Image
            src={`${assetPrefix}/static/images/demo-planner-flow.webp`}
            alt="AI Meal Planning"
            fill
            className="object-contain"
            priority
          />
        </div>
        <div className="flex-1 min-h-0">
          <Carousel
            setApi={setApi}
            opts={{
              loop: true,
              align: 'start',
            }}
            plugins={[
              Autoplay({
                delay: 3000,
                stopOnInteraction: false,
                stopOnMouseEnter: true,
              }),
            ]}
            className="size-full"
          >
            <CarouselContent className="h-full">
              <CarouselItem>
                <IntroSlide1 />
              </CarouselItem>
              <CarouselItem>
                <IntroSlide2 />
              </CarouselItem>
            </CarouselContent>
          </Carousel>
        </div>

        {/* Bottom Section - stays at bottom */}
        <div className="shrink-0 pb-4 px-4">
          {/* Pagination Dots */}
          <div className="flex justify-center gap-2 py-4">
            <button
              onClick={() => scrollTo(0)}
              className={cn(
                'h-1.5 rounded-full transition-all w-10',
                currentSlide === 0 ? 'bg-primary' : 'bg-white/30'
              )}
            />
            <button
              onClick={() => scrollTo(1)}
              className={cn(
                'h-1.5 rounded-full transition-all w-10',
                currentSlide === 1 ? 'bg-primary' : 'bg-white/30'
              )}
            />
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <p className="text-white/60 text-xs mb-4">
              Bạn hay đói đến trưa mới nghĩ hôm nay ăn gì?
              <br />
              Hãy thiết lập hồ sơ để chúng tôi gợi ý!
            </p>
          </div>
        </div>
        {/* Spacer for bottom button */}
        <div className="h-24" />
      </ScrollArea>
      <div className="fixed bottom-0 inset-x-0 pb-4 pt-2 px-4">
        <div className=" glass-bubble rounded-full flex justify-center px-4 py-2 h-16">
          <Button
            onClick={handleStart}
            className="size-full rounded-full text-lg font-semibold text-white bg-transparent"
          >
            Bắt đầu ngay
          </Button>
        </div>
      </div>
    </div>
  );
}
